import React, { useCallback, useEffect, useState } from 'react';
import { fetchYieldSheet, yieldWrite } from '../services/api';
import { buildSummary, money, num2, summaryTables, summaryWarnings } from './yieldSheetCalc';
import { YieldFlagNote } from './YieldSheetEditor';

/**
 * The admin portal's Yield Summary pop-up: valuation, yield and retail ratios for one sheet, the
 * retail price edit, Print and Send Email. Azure exit WS8.
 */

// Prints one block of the page and nothing around it. App.css has the matching print rules.
export function printArea(id) {
  const el = document.getElementById(id);
  if (!el) return;
  document.body.classList.add('ys-printing');
  el.classList.add('ys-print-area');
  window.print();
  el.classList.remove('ys-print-area');
  document.body.classList.remove('ys-printing');
}

function GradedValue({ title, rows, total, end, live, onPrice, onSavePrice }) {
  return (
    <table className="trans-table ys-sum">
      <thead><tr><th>{title} Steam Count (Start)</th><th>STEAM</th><th>Live</th><th>Total</th><th>Crab Place Retail ($)</th><th>Valuation</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.itemId}>
            <td>{r.gradedby}</td><td>{num2(r.tallySum)}</td><td>{num2(r.live)}</td><td>{num2(r.tallySum + r.live)}</td>
            <td>
              <span className="ys-print-only">{r.retailPrice}</span>
              <div className="ys-price hide-in-print">
                <input type="number" className="form-input" value={r.retailPrice ?? ''} disabled={!live} aria-label="Retail price" onChange={(e) => onPrice(r.itemId, e.target.value === '' ? null : parseFloat(e.target.value))} />
                <button type="button" className="btn btn-outline" disabled={!live} onClick={() => onSavePrice(r)}>Save</button>
              </div>
            </td>
            <td>{money((r.tallySum + r.live) * r.retailPrice)}</td>
          </tr>
        ))}
        <tr><td colSpan={3}>{title} Steam Count (End)</td><td>{num2(end)}</td><td /><td /></tr>
        <tr><td colSpan={5}><strong>Total {title} Value</strong></td><td><strong>{money(total)}</strong></td></tr>
      </tbody>
    </table>
  );
}

export default function YieldSheetSummary({ id, supplierName, graded, live, onClose }) {
  const [s, setS] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => fetchYieldSheet(id).then((data) => setS(buildSummary(data, graded))).catch((e) => setMessage({ text: e.message })), [id, graded]);
  useEffect(() => { load(); }, [load]);

  const close = () => { if (window.confirm('Do you want to close this modal?')) onClose(); };

  if (!s) {
    return <div className="modal-overlay"><div className="modal ys-modal"><div className="ys-status">{message ? message.text : 'Loading...'}</div><div className="modal-footer"><button type="button" className="btn btn-outline" onClick={onClose}>Close</button></div></div></div>;
  }

  const setPrice = (itemId, retailPrice) => {
    const patch = (rows) => rows.map((r) => (r.itemId === itemId ? { ...r, retailPrice } : r));
    setS({ ...s, maleGradedTableData: patch(s.maleGradedTableData), femaleGradedTableData: patch(s.femaleGradedTableData) });
  };

  const savePrice = async (row) => {
    try {
      await yieldWrite('yield_retail_price', { yieldSheetItemId: row.itemId, retailPrice: row.retailPrice });
      setMessage({ text: 'Saved', good: true });
    } catch (e) { setMessage({ text: e.message }); }
    load();
  };

  const send = async () => {
    if (!window.confirm('Are you sure you want to send the email?')) return;
    try {
      const { subject, body } = summaryWarnings(s);
      const r = await yieldWrite('yield_report_email', { vendor: supplierName, dateReceived: s.dateReceived, subject, body, tables: summaryTables(s, supplierName), note: s.note });
      setMessage(r.ok ? { text: 'Email sent successfully', good: true } : { text: 'Failed to upload the report' });
    } catch (e) { setMessage({ text: e.message }); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal ys-modal">
        <div className="modal-header"><h2>Yield Summary</h2><button type="button" className="modal-close" aria-label="Close" onClick={close}>&times;</button></div>
        <div className="modal-body">
          <YieldFlagNote live={live} />
          {message && <div className={`ys-note ${message.good ? 'ys-note-good' : 'ys-note-bad'}`}>{message.text}</div>}
          <div id="ys-print-section">
            <div className="ys-letterhead">
              <strong>The Crab Place</strong><div>504 Maryland Avenue</div><div>Crisfield, MD 21817</div><div>Phone: 410-968-3167</div>
            </div>
            <div className="port-scroll">
              <table className="trans-table ys-sum">
                <thead>
                  <tr><th colSpan={2}>Live Count (Start)</th><th>Date:</th><th>{s.dateReceived}</th></tr>
                  <tr><th>Vendor Name</th><th colSpan={2}>{supplierName}</th><th>Total</th></tr>
                </thead>
                <tbody>
                  <tr><td>Total Count</td><td colSpan={2}>{s.totalBoxOrBushels}</td><td>{s.totalBoxOrBushels}</td></tr>
                  {s.purchaseOrderItems.map((x) => <tr key={x.description}><td>#{x.description}</td><td colSpan={2}>{x.qty}</td><td>{x.qty}</td></tr>)}
                  <tr><td>Dead</td><td colSpan={2}>{s.dead}</td><td>{s.dead}</td></tr>
                  <tr><td colSpan={3} style={{ textAlign: 'right' }}>Total</td><td>{s.totalBoxOrBushels - s.dead}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="port-scroll">
              <table className="trans-table ys-sum">
                <thead>
                  <tr><th colSpan={6} style={{ textAlign: 'right' }}>Validated Invoice Amount</th><th>{money(s.invoiceAmount)}</th></tr>
                  <tr><th>Crab Size</th><th>Box/Bushel Weight</th><th>Price</th><th>Invoiced Price</th><th>Transportation Cost</th><th>Total Price w/ Freight</th><th aria-label="Blank" /></tr>
                </thead>
                <tbody>
                  {s.validateInvoiceAmountData.map((x) => <tr key={x.size}><td>#{x.size}</td><td>{x.weight}</td><td>{money(x.price)}</td><td>{money(x.totalPrice)}</td><td>{money(x.transportationCost)}</td><td>{money(x.freight)}</td><td /></tr>)}
                  <tr><th>Invoice Total</th><th aria-label="Blank" /><td /><td>{money(s.itemsTotalPrice)}</td><td>{money(s.totalTransportationCost)}</td><td>{money(s.totalFreight)}</td><td /></tr>
                </tbody>
              </table>
            </div>
            <div className="port-scroll"><GradedValue title="Male" rows={s.maleGradedTableData} total={s.totalMaleSteamValue} end={s.totalMaleSteam} live={live} onPrice={setPrice} onSavePrice={savePrice} /></div>
            <div className="port-scroll"><GradedValue title="Female" rows={s.femaleGradedTableData} total={s.totalFemaleSteamValue} end={s.totalFemaleSteam} live={live} onPrice={setPrice} onSavePrice={savePrice} /></div>
            <table className="trans-table ys-sum"><thead><tr><th>Total Invoice Value</th><th>{money(s.totalMaleSteamValue + s.totalFemaleSteamValue)}</th></tr></thead></table>
            <div className="port-scroll">
              <table className="trans-table ys-sum">
                <thead><tr><th>Male Count</th><th>End Count</th><th>Female Count</th><th>End Count</th></tr></thead>
                <tbody><tr><td>{num2(s.stratMaleCount)}</td><td>{num2(s.totalMaleSteam)}</td><td>{num2(s.startFemaleCount)}</td><td>{num2(s.totalFemaleSteam)}</td></tr></tbody>
              </table>
            </div>
            <div className="port-scroll">
              <table className="trans-table ys-sum">
                <thead><tr><th>Male Yield %</th><th>Female Yield %</th><th>Total Yield %</th><th>Retail Ratio(Males) Goal 100%+</th><th>Retail Ratio(Females) Goal 100%+</th></tr></thead>
                <tbody>
                  <tr>
                    <td>{num2((s.totalMaleSteam / s.stratMaleCount) * 100)} %</td><td>{num2((s.totalFemaleSteam / s.startFemaleCount) * 100)} %</td>
                    <td>{num2(((s.totalFemaleSteam + s.totalMaleSteam) / (s.startFemaleCount + s.stratMaleCount)) * 100)} %</td>
                    <td>{num2((s.totalMaleSteamValue / s.totalMaleFreight - 1) * 100)} %</td><td>{num2((s.totalFemaleSteamValue / s.totalFemaleFreight - 1) * 100)} %</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <table className="trans-table ys-sum"><thead><tr><th>Note:</th><th>{s.note}</th></tr></thead></table>
          </div>
        </div>
        <div className="modal-footer ys-footer">
          <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
          <button type="button" className="btn btn-outline" disabled={!live || s.isFinilized} onClick={send}>Send Email</button>
          <button type="button" className="btn btn-primary" onClick={() => printArea('ys-print-section')}>Print</button>
        </div>
      </div>
    </div>
  );
}